import Input from "components/Input";
import styles from "./styles.module.scss";
import { FC, IUpeOffice, Props } from "./typings";
import { useEffect, useState } from "react";
import Button from "components/Button";
import settingsService, {
  IConfigureIntegrationRequest,
} from "api/settingsService";
import { axiosInstance } from "api/axios";
import { ENotificationType, notification } from "utils/notification";
import HollowButton from "components/HollowButton";
import { IntegrationType } from "dto/Settings/IntegrationType";
import { IntegrationProvider } from "dto/Settings/IntegrationProvider";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "utils/getErrorMessage";

const noAutofillProps = (name: string, autoComplete = "off") => ({
  autoComplete,
  name,
  "data-lpignore": "true",
  "data-form-type": "other",
});

// Konfiguracja Urzędowej Poczty Elektronicznej. W Magistracie dane logowania podawało się
// w pierwszym kroku kreatora wniosku o publikację — przy każdej wysyłce. Tutaj zestawia się
// je raz, a kreator zaczyna od razu od rodzaju wniosku.
//
// Wystawca dziennika (urząd wojewódzki) wyznacza zarówno bazę UPE, jak i adres usługi;
// pole „adres usługi" zostaje na wypadek instancji spoza standardowej listy.
const ConfigureUpe: FC<Props> = ({ onSuccess, toggle }) => {
  const { t } = useTranslation();

  const { mutate: integration, isLoading } =
    settingsService.useConfigureIntegration();

  const [offices, setOffices] = useState<IUpeOffice[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [data, setData] = useState({
    systemOfficeName: "",
    login: "",
    password: "",
    serviceUrl: "",
  });

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    axiosInstance
      .get<IUpeOffice[]>("/settings/upe/offices")
      .then((response) => {
        if (active) setOffices(response.data ?? []);
      })
      .catch(() => {
        if (active)
          setError(
            "Nie udało się pobrać listy wystawców dzienników urzędowych.",
          );
      });

    return () => {
      active = false;
    };
  }, []);

  const selected = offices.find((x) => x.SystemName === data.systemOfficeName);

  // Wystawca dziennika jest opcjonalny — usługa wiąże konto z instytucją po loginie
  // (sprawdzone na żywej usłudze), więc do zestawienia integracji wystarczą dane logowania.
  const isComplete = !!data.login.trim() && !!data.password;

  const handleClick = () => {
    const payload: IConfigureIntegrationRequest = {
      Type: IntegrationType.Upe,
      Provider: IntegrationProvider.Upe,
      UpeSystemOfficeName: data.systemOfficeName,
      UpeLogin: data.login.trim(),
      UpePassword: data.password,
      UpeServiceUrl: data.serviceUrl.trim() || undefined,
    };

    setError(null);
    integration(payload, {
      onSuccess: () => {
        onSuccess(selected?.Name ?? data.systemOfficeName);
        toggle();
      },
      onError: (response) => {
        const message = getErrorMessage(response);

        notification({
          type: ENotificationType.ERROR,
          title: t("integrations.createIntegrationFailed"),
          text: message,
        });
        setError(message);
      },
    });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>Konfiguracja UPE</div>

      <div className={styles.body}>
        Urzędowa Poczta Elektroniczna służy do składania wniosków o ogłoszenie
        aktów prawa miejscowego w wojewódzkim dzienniku urzędowym. Podaj dane
        logowania do UPE — połączenie zostanie od razu sprawdzone, a nadawcę
        i adresata usługa ustali sama na podstawie konta.
      </div>

      <div className={styles.form}>
        <label className={styles.selectRow}>
          <span className={styles.selectLabel}>
            Wystawca dziennika (opcjonalnie)
          </span>
          <select
            className={styles.select}
            value={data.systemOfficeName}
            onChange={(e) =>
              setData({ ...data, systemOfficeName: e.target.value })
            }
          >
            <option value="">— ustali usługa na podstawie konta —</option>
            {offices
              .filter((x) => !x.IsTest)
              .map((office) => (
                <option key={office.SystemName} value={office.SystemName}>
                  {office.Name}
                </option>
              ))}
            {offices.some((x) => x.IsTest) && (
              <optgroup label="Środowiska producenta">
                {offices
                  .filter((x) => x.IsTest)
                  .map((office) => (
                    <option key={office.SystemName} value={office.SystemName}>
                      {office.Name}
                    </option>
                  ))}
              </optgroup>
            )}
          </select>
        </label>

        <div className={styles.hint}>
          Adres usługi: http://upe.com.pl/ws/UPEv4WebService.asmx — jeden wspólny dla
          wszystkich dzienników. Usługa nie obsługuje https, więc dane logowania nie są
          chronione szyfrowaniem połączenia; własny adres podasz w ustawieniach
          zaawansowanych.
        </div>

        <Input
          value={data.login}
          onChange={(v) => setData({ ...data, login: v })}
          label="Login"
          autoComplete="off"
          inputProps={noAutofillProps("upe-login-field")}
        />
        <Input
          value={data.password}
          type="password"
          onChange={(v) => setData({ ...data, password: v })}
          label="Hasło"
          autoComplete="new-password"
          inputProps={noAutofillProps("upe-password-field", "new-password")}
        />

        <button
          type="button"
          className={styles.advancedToggle}
          onClick={() => setShowAdvanced((x) => !x)}
        >
          {showAdvanced ? "Ukryj ustawienia zaawansowane" : "Ustawienia zaawansowane"}
        </button>

        {showAdvanced && (
          <Input
            value={data.serviceUrl}
            onChange={(v) => setData({ ...data, serviceUrl: v })}
            label="Adres usługi (opcjonalnie)"
            autoComplete="off"
            inputProps={noAutofillProps("upe-url-field")}
          />
        )}

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.buttonsWrapper}>
          <HollowButton text={t("common.cancel")} onClick={() => toggle()} />
          <Button
            text={t("common.save")}
            onClick={handleClick}
            loading={isLoading}
            disabled={isLoading || !isComplete}
          />
        </div>
      </div>
    </div>
  );
};

export default ConfigureUpe;
